import React, {
  useMemo, useState, forwardRef, useImperativeHandle, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import colors from '../constants/Colors';

import { downloadFile } from '../files/fileHelpers';
import EChart from '../common/charts/EChart';
import {
  buildColumnOption,
  buildLineOption,
  buildPieOption,
  buildStackedSteppedOption,
} from '../common/charts/chartOptions';

import {
  formatDate,
  getRowName,
  getDateRangeFromConfig,
  buildChartModel,
  buildOption,
} from './analytics.helpers';

import { getWeekStart } from '../schedule/scheduleHelpers';
import AnalyticsDetailDrawer from './AnalyticsDetailDrawer';

const AnalyticsChart = forwardRef(({
  fieldTitle,
  analyticsConfig,
  cardId,
}, ref) => {
  const {
    data = [],
    columns = [],
    dateRange: selectedRange,
    datePreset,
    dateBreakdown,
    breakdown = [],
    chartType: configChartType,
    fieldId,
  } = analyticsConfig ?? {};

  const [chart, setChart] = useState();
  const [drawerConfig, setDrawerConfig] = useState();

  useImperativeHandle(ref, () => ({
    export: (name) => {
      if (!chart) return;
      const image = chart.getDataURL({ type: 'png', backgroundColor: '#fff' });
      void downloadFile({
        fileURL: image,
        fileDetails: { name },
      });
    },
  }), [chart]);

  const dateRange = useMemo(() => (
    getDateRangeFromConfig({ dateRange: selectedRange, datePreset })
  ), [selectedRange, datePreset]);

  const chartModel = useMemo(() => buildChartModel({
    data,
    columns,
    dateRange,
    dateBreakdown,
    breakdown,
    fieldTitle,
    chartType: configChartType,
  }), [data, columns, dateRange, dateBreakdown, breakdown, fieldTitle, configChartType]);

  const option = useMemo(() => buildOption(configChartType, chartModel), [configChartType, chartModel]);

  const onCloseDrawer = useCallback(() => setDrawerConfig(), []);

  const onChartClick = useCallback((params) => {
    if (params?.componentType === 'series') {
      const { dataIndex, seriesIndex } = params;
      if (configChartType === 'pie') {
        const slice = chartModel.slices?.[dataIndex];
        if (slice) {
          setDrawerConfig({
            title: slice.name,
            ids: slice.ids,
          });
        }
      } else if (
        (configChartType === 'bar' || configChartType === 'line')
        && (fieldId === 'numberOfCards' || fieldId === 'numberOfSubmissions')
      ) {
        const pointSeries = chartModel.series?.[seriesIndex];
        setDrawerConfig({
          title: chartModel.categories?.[dataIndex],
          ids: pointSeries?.ids?.[dataIndex],
        });
      }
    }
  }, [chartModel, configChartType, fieldId]);

  const onEvents = useMemo(() => ({
    click: onChartClick,
  }), [onChartClick]);

  const onChartReady = useCallback((instance) => {
    setChart(instance);
    instance.resize();
  }, []);

  return (
    <>
      <div className="analytics-chart">
        <EChart
          option={option}
          onEvents={onEvents}
          onChartReady={onChartReady}
        />
      </div>
      <AnalyticsDetailDrawer
        config={drawerConfig}
        onClose={onCloseDrawer}
        analyticsConfig={analyticsConfig}
        cardId={cardId}
      />
    </>
  );
});

/* eslint-disable react/forbid-prop-types */
AnalyticsChart.propTypes = {
  fieldTitle: PropTypes.string,
  analyticsConfig: PropTypes.object,
  cardId: PropTypes.string,
};

AnalyticsChart.defaultProps = {
  fieldTitle: 'Data',
  analyticsConfig: {},
  cardId: undefined,
};

export default AnalyticsChart;
